import styled, { css } from "styled-components";

import { SWReactIcons } from "@similarweb/icons";
import { colorsPalettes, rgba, setFontVariant } from "@similarweb/styles";

import { aiAgentColors } from "app/pages/sales-intelligence/modules/ai-agent/components";

import type { GradientButtonVariant } from "./types";

const spacingByVariant: Record<GradientButtonVariant, { padding: string }> = {
    generate: { padding: "10px 16px" },
    ghost: { padding: "10px 16px" },
    submit: { padding: "8px" },
    disabled: { padding: "8px" },
    locked: { padding: "12px" },
    flat: { padding: "8px 12px" },
    flatRectangle: { padding: "8px 12px" },
    ghostSmall: { padding: "8px 16px" },
};

const BUTTON_GRADIENT = `linear-gradient(
    89.27deg,
    ${aiAgentColors.gradient.blue} 1.62%,
    ${aiAgentColors.gradient.lightBlue} 72.84%,
    ${aiAgentColors.gradient.green} 99.54%
)`;

const ghostButtonStyles = css`
    color: transparent;

    &:before {
        content: "";
        position: absolute;
        inset: 0;
        border-radius: 50px;
        background: linear-gradient(45deg, ${aiAgentColors.gradient.blue}, ${aiAgentColors.gradient.green});
    }

    &:after {
        content: "";
        position: absolute;
        inset: 2px;
        border-radius: 50px;
        background: ${colorsPalettes.carbon[0]};
    }

    & ${SWReactIcons} {
        z-index: 1;
    }
`;

export const StyledGradientButton = styled.button<{ variant: GradientButtonVariant; disabled?: boolean }>`
    ${setFontVariant("button")};

    position: relative;
    display: flex;
    height: 40px;
    gap: 10px;
    align-items: center;
    justify-content: center;
    padding: ${({ variant }) => spacingByVariant[variant].padding};
    margin: 0;
    color: ${colorsPalettes.carbon[0]};
    background: ${({ variant }) => {
        switch (variant) {
            case "ghost":
            case "flat":
            case "flatRectangle":
            case "ghostSmall":
                return "transparent";
            case "disabled":
                return colorsPalettes.carbon[100];
            default:
                return BUTTON_GRADIENT;
        }
    }};

    border-radius: 50px;
    border: none;

    ${({ variant }) => variant === "ghost" && ghostButtonStyles}

    ${({ variant }) =>
        variant === "ghostSmall" &&
        css`
            ${ghostButtonStyles};
            height: 36px;
            gap: 4px;

            &:after {
                inset: 1px;
            }

            & ${SWReactIcons} svg path {
                fill: ${aiAgentColors.gradient.blue};
            }

            &:hover {
                &:after {
                    background: ${colorsPalettes.bluegrey[100]};
                }
            }
        `}

    &:hover {
        background: ${({ variant }) =>
            variant === "flat" || variant === "flatRectangle"
                ? rgba(colorsPalettes.carbon[200], 0.2)
                : "linear-gradient(90deg, #5c8ee6 1%, #3cc7a5 100%)"};
    }
    &:active {
        background: ${({ variant }) =>
            variant === "flat" || variant === "flatRectangle"
                ? "#5FD5D933"
                : "linear-gradient(90deg, #4b75bf 1%, #31a588 100%)"};
    }

    ${({ disabled, variant }) =>
        disabled &&
        css`
            background: ${variant === "flat" || variant === "flatRectangle"
                ? "transparent"
                : colorsPalettes.carbon[100]};
            cursor: not-allowed;

            &:hover {
                background: ${variant === "flat" || variant === "flatRectangle"
                    ? "transparent"
                    : colorsPalettes.carbon[100]};
            }
            &:active {
                background: ${variant === "flat" || variant === "flatRectangle"
                    ? "transparent"
                    : colorsPalettes.carbon[100]};
            }
        `}
`;

export const StyledGradientButtonText = styled.span`
    z-index: 1;
    background-clip: text;
    color: transparent;
    background-image: linear-gradient(45deg, ${aiAgentColors.gradient.blue}, ${aiAgentColors.gradient.green});
`;

export const StyledRectangle = styled.div`
    box-sizing: border-box;
    width: 16px;
    height: 16px;
    border-radius: 1px;
    border: 2px solid ${colorsPalettes.carbon[0]};
`;

export const StyledGradientRectangle = styled.div`
    box-sizing: border-box;
    width: 16px;
    height: 16px;
    border-radius: 1px;
    background: transparent;
    border: 2px solid ${colorsPalettes.carbon[0]}; /* fallback */

    @supports (border-image: linear-gradient(${aiAgentColors.gradient.blue}, ${aiAgentColors.gradient.green})) {
        border: 2px solid transparent;
        border-image-slice: 1;
        border-image-source: linear-gradient(
            89.27deg,
            ${aiAgentColors.gradient.blue} 1.62%,
            ${aiAgentColors.gradient.lightBlue} 72.84%,
            ${aiAgentColors.gradient.green} 99.54%
        );
    }
`;
